import React, { useState, useEffect, useContext } from 'react';
import {
  RouteComponentProps,
  StaticContext,
  withRouter,
  useParams,
  useLocation,
} from 'react-router';
import { ModifiersUtils } from 'react-day-picker';
import SubmitButton from '../components/SubmitButton';
import Title from '../components/Title';
import AddressLookup, {
  Address,
  testPostCode,
  validateAddress,
  getCityFromPostcode,
} from '../components/AddressLookup';
import 'react-day-picker/lib/style.css';
import {
  BoxData,
  Product,
  Dimensions,
  BookingParams,
} from '../models/APIModels';
import Prices from '../components/Prices';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import DayPickerInputValue from '../components/edit/DayPickerInputValue';
import { useDateDisabler } from '../utilities/DateDisabler';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';
import { isOutOfArea, isDifferentArea } from '../utilities/Postcode';
import { toMoney } from '../utilities/MoneyConversion';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  REDELIVER_FEE,
  REDELIVER_SURCHARGE,
} from '../constants';
import { CalendarIcon } from '@heroicons/react/24/solid';

interface CollectionDeliveryProps
  extends RouteComponentProps<undefined, StaticContext, CollectionState> {}

interface CollectionState {
  boxes: BoxData[];
  collectionAddress?: Address;
  collectionDate?: Date;
  deliveryAddress?: Address;
  deliveryDate?: Date;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  dontKnowAddress?: boolean;
  selectedTier?: any;
  questions?: any;
  removalService?: boolean;
  moveDate?: Date;
  wantsStorage?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  isCollapsed: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
  collapsedNote?: string;
}

function CollapsibleSection({
  title,
  isCollapsed,
  onToggle,
  children,
  className = '',
  collapsedNote,
}: CollapsibleSectionProps) {
  return (
    <div
      className={`collapsible-section ${
        isCollapsed ? 'collapsed' : ''
      } ${className}`}
    >
      <div className="collapsible-header" onClick={onToggle}>
        <Title>{title}</Title>
        <button type="button" className="collapse-toggle">
          <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} />
        </button>
      </div>
      {isCollapsed && collapsedNote && <p className="grey">{collapsedNote}</p>}
      <div
        className={`collapsible-content ${
          isCollapsed ? 'collapsed' : 'expanded'
        }`}
      >
        {children}
      </div>
    </div>
  );
}

function validateCollection(
  collectionAddress: Address,
  collectionDate: Date | undefined,
  disabledDays: any[],
) {
  return (
    validateAddress(collectionAddress) &&
    collectionDate &&
    !ModifiersUtils.dayMatchesModifier(collectionDate, disabledDays)
  );
}

function CollectionDeliveryComponent({
  history,
}: CollectionDeliveryProps): JSX.Element {
  const location = useLocation();
  const { route } = useParams<BookingParams>();
  
  const locationState = history.location.state || {};
  
  const getStorageKey = () => {
    if (location.pathname.includes('/removals')) return 'removals-state';
    if (location.pathname.includes('/storage')) return 'storage-state';
    if (location.pathname.includes('/shipping')) return 'shipping-state';
    if (location.pathname.includes('/suitcase')) return 'suitcase-state';
    return 'collection-delivery-state';
  };
  
  const storageKey = getStorageKey();
  
  const localStorageState = (() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  })();
  
  const combinedState = { ...localStorageState, ...locationState };
  
  const { 
    boxes, 
    storageRequired, 
    suitcases, 
    suitcaseData,
    selectedTier,
    questions,
    removalService,
    moveDate: initialMoveDate,
    wantsStorage,
    collectionAddress: initialCollectionAddress,
    collectionDate: initialCollectionDate,
    deliveryAddress: initialDeliveryAddress,
    deliveryDate: initialDeliveryDate,
    dontKnowAddress: initialDontKnowAddress,
  } = combinedState;
  
  const parseDate = (date: any): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  };
  
  const isRemovalsFlow = location.pathname.includes('/removals') || 
                        removalService;
  
  const [collectionAddress, setCollectionAddressState] = useState<Address>(initialCollectionAddress || {});
  const [collectionDate, setCollectionDate] = useState<Date | undefined>(parseDate(initialCollectionDate));
  const [deliveryAddress, setDeliveryAddressState] = useState<Address>(initialDeliveryAddress || {});
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(parseDate(initialDeliveryDate));
  const [dontKnowDeliveryAddress, setDontKnowDeliveryAddress] = useState<boolean>(initialDontKnowAddress || false);
  const [moveDate, setMoveDate] = useState<Date | undefined>(parseDate(initialMoveDate));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const stateToSave = {
      boxes,
      collectionAddress,
      collectionDate,
      deliveryAddress: ((storageRequired || isRemovalsFlow) && (dontKnowDeliveryAddress || !deliveryAddress.postCode)) ? {} : deliveryAddress,
      deliveryDate,
      dontKnowAddress: (storageRequired || isRemovalsFlow) && (dontKnowDeliveryAddress || !deliveryAddress.postCode),
      storageRequired,
      suitcases,
      suitcaseData,
      selectedTier,
      questions,
      removalService,
      moveDate,
      wantsStorage,
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      localStorage.setItem('payment-state', JSON.stringify(stateToSave));
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }, [collectionAddress, collectionDate, deliveryAddress, deliveryDate, dontKnowDeliveryAddress, moveDate, storageKey, boxes, storageRequired, suitcases, suitcaseData, selectedTier, questions, removalService, wantsStorage, isRemovalsFlow]);

  // Wrapper functions for AddressLookup component
  const setCollectionAddress = (address: Address, addressString: string) => {
    setCollectionAddressState(address);
  };

  const setDeliveryAddress = (address: Address, addressString: string) => {
    setDeliveryAddressState(address);
  };

  // Add validation error states for date pickers
  const [collectionDateError, setCollectionDateError] = useState<string | null>(
    null,
  );
  const [deliveryDateError, setDeliveryDateError] = useState<string | null>(
    null,
  );
  const [moveDateError, setMoveDateError] = useState<string | null>(null);
  const [differentCityError, setDifferentCityError] = useState<string | null>(null);

  const [isCollectionCollapsed, setIsCollectionCollapsed] = useState(false);
  const [isReturnCollapsed, setIsReturnCollapsed] = useState(
    storageRequired ? true : false,
  );

  const collectionDisabledDays = useDateDisabler(null, null);
  const deliveryDisabledDays = useDateDisabler(
    deliveryAddress,
    collectionDate ?? null,
  );
  // For removals, use tier-based buffer: 3 days for Mini/Small, 7 days for others
  const getMoveDateBuffer = () => {
    if (!isRemovalsFlow) return undefined;
    if (selectedTier) {
      const tierName = selectedTier.name?.toLowerCase() || '';
      if (tierName.includes('mini') || tierName.includes('small')) {
        return 3;
      }
    }
    return 7;
  };
  const baseMoveDateDisabledDays = useDateDisabler(null, null, getMoveDateBuffer());

  // For Mini/Small tiers in Cambridge or London, enable Saturdays
  const moveDisabledDays = React.useMemo(() => {
    if (!isRemovalsFlow || !selectedTier) return baseMoveDateDisabledDays;

    const tierName = selectedTier.name?.toLowerCase() || '';
    const isMiniOrSmall = tierName.includes('mini') || tierName.includes('small');
    const collectionCity = collectionAddress.postCode ? getCityFromPostcode(collectionAddress.postCode) : '';
    const isCambridgeOrLondon = collectionCity === 'Cambridge' || collectionCity === 'London';

    // If Mini/Small and in Cambridge or London, remove Saturday restriction
    if (isMiniOrSmall && isCambridgeOrLondon) {
      return baseMoveDateDisabledDays.filter((day: any) => {
        // Keep all disabled days except the one that disables Saturdays
        if (day.daysOfWeek && Array.isArray(day.daysOfWeek)) {
          // Only disable Sundays (0), not Saturdays (6)
          return false;
        }
        return true;
      }).concat([{ daysOfWeek: [0] }]); // Only disable Sundays
    }

    return baseMoveDateDisabledDays;
  }, [isRemovalsFlow, selectedTier, collectionAddress.postCode, baseMoveDateDisabledDays]);

  const isCollectionValid = isRemovalsFlow
    ? validateAddress(collectionAddress)
    : validateCollection(
      collectionAddress,
      collectionDate,
      collectionDisabledDays,
    ) && !collectionDateError; // Include date picker validation

  const isDeliveryValid = isRemovalsFlow
    ? (wantsStorage ? (dontKnowDeliveryAddress || validateAddress(deliveryAddress)) : validateAddress(deliveryAddress))
    : storageRequired
    ? !deliveryAddress.postCode ||
      (validateAddress(deliveryAddress) &&
        (!deliveryDate ||
          (deliveryDate &&
            !ModifiersUtils.dayMatchesModifier(
              deliveryDate,
              deliveryDisabledDays,
            ) &&
            !deliveryDateError))) // Delivery date is optional for storage
    : validateAddress(deliveryAddress) && deliveryAddress.postCode && deliveryAddress.line1; // Shipping: MUST have valid delivery address

  const isMoveDateValid = isRemovalsFlow 
    ? moveDate && !ModifiersUtils.dayMatchesModifier(moveDate, moveDisabledDays) && !moveDateError
    : true;

  // Check if delivery city is different from collection city (for removals)
  useEffect(() => {
    if (isRemovalsFlow && collectionAddress.postCode && deliveryAddress.postCode && !dontKnowDeliveryAddress) {
      const collectionCity = getCityFromPostcode(collectionAddress.postCode);
      const deliveryCity = getCityFromPostcode(deliveryAddress.postCode);
      
      if (collectionCity && deliveryCity && collectionCity !== deliveryCity) {
        setDifferentCityError(
          `Oops! Looks like you've selected a different city / long journey, this will need a custom quote. Contact us for a quote.`
        );
      } else {
        setDifferentCityError(null);
      }
    } else {
      setDifferentCityError(null);
    }
  }, [isRemovalsFlow, collectionAddress.postCode, deliveryAddress.postCode, dontKnowDeliveryAddress]);

  const isValid = isCollectionValid && isDeliveryValid && isMoveDateValid && !differentCityError;

  const { user } = useContext(UserContext);

  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_collection_delivery_address' });
    window.scrollTo(0, 0);
  }, [gtmEventTracking]);

  const handleCollectionToggle = () => {
    if (isCollectionCollapsed) {
      setIsCollectionCollapsed(false);
      if (storageRequired) {
        setIsReturnCollapsed(true);
      }
    } else {
      setIsCollectionCollapsed(true);
    }
  };

  const handleReturnToggle = () => {
    if (isReturnCollapsed) {
      setIsReturnCollapsed(false);
      if (storageRequired) {
        setIsCollectionCollapsed(true);
      }
    } else {
      setIsReturnCollapsed(true);
    }
  };

  return (
    <>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>

      <NavigationStatus 
        step={isRemovalsFlow ? 4 : 2} 
        locationState={history.location.state}
      />
      <form
        onSubmit={(event) => {
          setIsSubmitting(true);
          event.preventDefault();
          if (isValid) {
            const nextRoute = isRemovalsFlow ? '/removals/payment' : `/${route}/payment`;
            history.push(nextRoute, {
              boxes,
              collectionAddress,
              collectionDate,
              deliveryAddress: ((storageRequired || isRemovalsFlow) && (dontKnowDeliveryAddress || !deliveryAddress.postCode)) ? {} : deliveryAddress,
              deliveryDate,
              dontKnowAddress: (storageRequired || isRemovalsFlow) && (dontKnowDeliveryAddress || !deliveryAddress.postCode),
              storageRequired,
              suitcases,
              suitcaseData,
              selectedTier,
              questions,
              removalService,
              moveDate,
              wantsStorage,
            });
          } else {
            setIsSubmitting(false); // Reset submitting state if validation fails
          }
        }}
      >
        {isRemovalsFlow ? (
          <div className="removals-addresses-container">
            <div className="removals-addresses-wrapper">
              <div className="removal-address-section">
                <Title>Where are you moving from?</Title>
                <AddressLookup
                  address={collectionAddress}
                  setAddress={setCollectionAddress}
                  onlyUnitedKingdom
                  idPrefix="collection"
                  allowedCities={['London', 'Exeter', 'Sheffield', 'Cambridge']}
                />
              </div>

              <div className="removal-address-section">
                <Title>Where are you moving to?</Title>
                {!wantsStorage && (
                  <p className="grey removal-description" style={{ marginBottom: '10px' }}>
                    Delivery address is required for this move
                  </p>
                )}
                {!dontKnowDeliveryAddress ? (
                  <>
                    <AddressLookup
                      address={deliveryAddress}
                      setAddress={setDeliveryAddress}
                      onlyUnitedKingdom
                      idPrefix="delivery"
                      allowedCities={['London', 'Exeter', 'Sheffield', 'Cambridge']}
                    />
                    {wantsStorage && deliveryAddress.postCode && (
                      <div style={{
                        marginTop: '15px',
                        padding: '12px 16px',
                        backgroundColor: '#f0f7ff',
                        borderRadius: '8px',
                        border: '1px solid #b8daff',
                        fontSize: '14px',
                        color: '#004085',
                      }}>
                        <strong>Return Delivery Fee:</strong> A delivery charge equivalent to your collection charge will be processed 5 days before the return date. If the delivery address is in a different city to your collection address, a bespoke return fee will be calculated.
                      </div>
                    )}
                    {wantsStorage && (
                      <button
                        type="button"
                        className="btn-secondary dont-know-btn"
                        onClick={() => {
                          setDontKnowDeliveryAddress(true);
                          setDeliveryAddressState({});
                        }}
                        style={{
                          marginTop: '15px',
                          padding: '10px 20px',
                          backgroundColor: 'var(--greyLight)',
                          border: '1px solid var(--grey)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: 'var(--black)',
                          transition: 'all 0.2s ease',
                        }}
                      >
                        Don't know yet
                      </button>
                    )}
                  </>
                ) : (
                  <div style={{
                    padding: '20px',
                    backgroundColor: '#f5f3ff',
                    borderRadius: '12px',
                    border: '2px solid var(--kkUltraVioletVeryLight)',
                    textAlign: 'center',
                  }}>
                    <p style={{ marginBottom: '15px', color: '#555' }}>
                      No problem! You can provide this information later.
                    </p>
                    <div style={{
                      marginBottom: '15px',
                      padding: '12px 16px',
                      backgroundColor: '#f0f7ff',
                      borderRadius: '8px',
                      border: '1px solid #b8daff',
                      fontSize: '14px',
                      color: '#004085',
                    }}>
                      <strong>Note:</strong> Please note for the price shown you'll only be able to move within the same city.
                    </div>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={() => {
                        setDontKnowDeliveryAddress(false);
                      }}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'var(--kkUltraViolet)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      Enter delivery address now
                    </button>
                  </div>
                )}
              </div>
            </div>

            {differentCityError && (
              <div style={{
                marginTop: '20px',
                padding: '16px 20px',
                backgroundColor: '#fff3cd',
                borderRadius: '12px',
                border: '2px solid #ffc107',
                fontSize: '15px',
                fontWeight: '500',
                color: '#856404',
                textAlign: 'center',
              }}>
                ⚠️ {differentCityError}
              </div>
            )}

            <div className="removal-address-section">
              <Title>Desired move date</Title>
              <p className="grey removal-description">
                When would you like to move?
              </p>
              <div style={{
                marginTop: '8px',
                marginBottom: '12px',
                padding: '10px 14px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                fontSize: '13px',
                color: '#666',
              }}>
                <strong>Note:</strong> Please note this isn't guaranteed.
              </div>
              <div className="coll-del-div addressLookup" style={{ minHeight: '120px', overflow: 'visible' }}>
                <div className="title-and-input">
                  <div className="title">
                    <section>Move Date:</section>
                    <CalendarIcon
                      style={{ width: '30px', height: '30px', color: '#6c3cd9' }}
                    />
                  </div>

                  <DayPickerInputValue
                    id="moveDate"
                    handleStateChange={(day) => setMoveDate(day as any)}
                    disabled={false}
                    disabledDays={moveDisabledDays}
                    onValidationError={setMoveDateError}
                    value={moveDate ? moveDate.getTime() : undefined}
                  />
                </div>
              </div>
              <div style={{
                marginTop: '12px',
                padding: '12px 16px',
                backgroundColor: '#fff9e6',
                borderRadius: '8px',
                border: '1px solid #ffd966',
                fontSize: '13px',
                color: '#856404',
              }}>
                <p style={{ marginBottom: '8px' }}><strong>Weekend Surcharges:</strong></p>
                <ul style={{ marginLeft: '20px', marginBottom: '0' }}>
                  <li>Friday moves incur an additional surcharge</li>
                  <li>Saturday moves incur an additional surcharge</li>
                  {selectedTier && (selectedTier.name?.toLowerCase().includes('mini') || selectedTier.name?.toLowerCase().includes('small')) && (
                    <li>Saturday moves are available for Mini/Small packages in Cambridge and London</li>
                  )}
                </ul>
              </div>
            </div>

            {/* Pricing Summary Section */}
            {selectedTier && (
              <div style={{
                marginTop: '24px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '2px solid #dee2e6',
              }}>
                <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Price Summary</h3>

                <div style={{ marginBottom: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span><strong>{selectedTier.name}</strong></span>
                    <span style={{ fontWeight: '600', fontSize: '18px' }}>£{(selectedTier.price / 100).toFixed(2)}</span>
                  </div>
                  <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>{selectedTier.description}</p>
                  <p style={{ fontSize: '13px', color: '#666' }}>{selectedTier.details}</p>
                </div>

                {wantsStorage && (
                  <>
                    <div style={{
                      marginTop: '16px',
                      padding: '12px',
                      backgroundColor: '#e7f3ff',
                      borderRadius: '8px',
                      border: '1px solid #b8daff',
                      fontSize: '13px',
                    }}>
                      <p style={{ marginBottom: '8px' }}><strong>Return Delivery Information:</strong></p>
                      <ul style={{ marginLeft: '20px', marginBottom: '0' }}>
                        <li>Return delivery charge equals your collection charge (£{(selectedTier.price / 100).toFixed(2)})</li>
                        <li>Return fee applies to moves within a 25-mile radius / same city</li>
                        <li>Different city returns require a bespoke quote</li>
                        <li>Minimum 7 working days notice required for return delivery</li>
                        <li>Return charge processed 5 days before scheduled return date</li>
                      </ul>
                    </div>
                  </>
                )}

                {/* Important Terms and Conditions */}
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: '#fff3cd',
                  borderRadius: '8px',
                  border: '2px solid #ffc107',
                  fontSize: '13px',
                }}>
                  <p style={{ marginBottom: '12px', fontWeight: '600', fontSize: '14px' }}>⚠️ Important Terms & Conditions</p>
                  <ul style={{ marginLeft: '20px', marginBottom: '0', lineHeight: '1.6' }}>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>Time Limits:</strong> This booking is for the package selected. Any time over the hours booked will incur additional charges. A minimum charge of £350 applies if the move exceeds the hours booked.
                    </li>
                    <li style={{ marginBottom: '8px' }}>
                      <strong>Space Limits:</strong> This move has been booked for the space/package option selected. Should the move exceed the size booked, Keepr reserves the right to pack only the space booked. You will need to rebook for Keepr to return on a different date if the remaining items cannot be collected on the day.
                    </li>
                    <li>
                      <strong>Service Area:</strong> The price shown applies to moves within a 25-mile radius / same city. Long distance or different city moves require a bespoke quote.
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <CollapsibleSection
              title="Collection Address"
              isCollapsed={isCollectionCollapsed}
              onToggle={handleCollectionToggle}
              className={isCollectionValid ? 'valid' : ''}
            >
              {route !== 'suitcase' && (
                <p className="grey">
                  We'll collect your belongings from here <br />
                  This is where your packing materials will be delivered 📦
                </p>
              )}
              <div className="all-coll-del">
                <AddressLookup
                  address={collectionAddress}
                  setAddress={setCollectionAddress}
                  onlyUnitedKingdom
                  idPrefix="collection"
                  label="Collection Address"
                >
                  {storageRequired &&
                    collectionAddress.postCode &&
                    isOutOfArea(collectionAddress.postCode) && (
                      <p>There is a surcharge in this area of £5.99</p>
                    )}
                </AddressLookup>
                <div className="coll-del-div">
                  <div className="title-and-input">
                    <div className="title">
                      <section>Collection Date:</section>
                      <CalendarIcon
                        style={{ width: '30px', height: '30px', color: '#6c3cd9' }}
                      />
                    </div>

                    <DayPickerInputValue
                      id="collectionDate"
                      value={collectionDate ? collectionDate.getTime() : undefined}
                      handleStateChange={(day) => setCollectionDate(day as any)}
                      disabled={false}
                      disabledDays={collectionDisabledDays}
                      onValidationError={setCollectionDateError}
                    />
                  </div>
                  <div className="description">
                    {!suitcases && (
                      <p className="grey">
                        <span>Note:</span> Your empty boxes will be delivered here.
                      </p>
                    )}
                    <p className="grey">
                      For collection, please make sure your boxes are on the ground
                      floor.
                    </p>
                  </div>
                </div>
              </div>

              <p>{testPostCode(collectionAddress)}</p>
            </CollapsibleSection>

            <CollapsibleSection
              title={storageRequired ? 'Return Address' : 'Delivery Address'}
              isCollapsed={isReturnCollapsed}
              onToggle={handleReturnToggle}
              className={isDeliveryValid ? 'valid' : ''}
              collapsedNote={
                storageRequired
                  ? '(Optional) This is where belongings will be returned, you can tell us this later'
                  : undefined
              }
            >
              <p className="grey">
                {storageRequired
                  ? '(Optional) This is where belongings will be returned, you can tell us this later'
                  : 'This is where your belongings will be delivered to'}
              </p>

              <div className="all-coll-del">
                <AddressLookup
                  address={deliveryAddress}
                  setAddress={setDeliveryAddress}
                  onlyUnitedKingdom
                  idPrefix="delivery"
                  label="Return Address"
                >
                  {storageRequired &&
                    deliveryAddress.postCode &&
                    collectionAddress.postCode &&
                    isDifferentArea(
                      deliveryAddress.postCode,
                      collectionAddress.postCode,
                    ) && (
                      <p>
                        There is a surcharge of £3.99 per box to deliver to a
                        different area.
                      </p>
                    )}
                </AddressLookup>

                {storageRequired && (
                  <div className="coll-del-div">
                    <div className="title-and-input">
                      <div className="title">
                        <section>Delivery Date:</section>
                        <CalendarIcon
                          style={{
                            width: '30px',
                            height: '30px',
                            color: '#6c3cd9',
                          }}
                        />
                      </div>

                      <DayPickerInputValue
                        id="deliveryDate"
                        value={deliveryDate ? deliveryDate.getTime() : undefined}
                        handleStateChange={(day) => setDeliveryDate(day as any)}
                        disabled={false}
                        disabledDays={deliveryDisabledDays}
                        onValidationError={setDeliveryDateError}
                      />
                    </div>
                    <div className="description">
                      <p className="grey">
                        <span>Note:</span> Delivery date is optional - you can order your boxes back at any time.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>
          </>
        )}

        {isRemovalsFlow && selectedTier ? (
          <div className="payment-figure">
            <h4>Total</h4>
            <span className="cost">£{(selectedTier.price / 100).toFixed(2)}</span>
            <div className="removal-details">
              <p>{selectedTier.description}</p>
              <p>{selectedTier.details}</p>
            </div>
          </div>
        ) : (
          <Prices
            boxes={boxes || []}
            storageRequired={storageRequired}
            suitcases={suitcases}
            suitcaseShippingCost={suitcaseData?.shippingCost}
          />
        )}

        <SubmitButton disabled={!isValid} isSubmitting={isSubmitting}>
          Next
          <img alt="" src="/img/right-arrow-circular-button-outline.png" />
        </SubmitButton>

        {storageRequired && (
          <p className="grey">
            Collection of boxes: {toMoney(COLLECTION_FEE)}
            <br />A {toMoney(COLLECTION_SURCHARGE)} surcharge may apply for some
            collection areas.
            <br />
            Redelivery of boxes: {toMoney(REDELIVER_FEE)} per box, to be paid at
            the end of storage period. (1 month minimum storage charge.)
            <br />
            Surcharge of {toMoney(REDELIVER_SURCHARGE)} per box may apply for
            delivering boxes to a city different from the pickup address.
          </p>
        )}
      </form>
    </>
  );
}

export default withRouter(CollectionDeliveryComponent);
